/**
 * Maps Appeal Summary form values to backend `pharmacy/appeal/update` fields.
 */

import { UpdateAppealPayload } from "../../../../api/postCall/updateAppeal";
import { CallingFormData } from "../../../sidePanel/submissionSummarySidePanel/utils/buildCallingFormDataFromOrder";
import { convertFormDateToStorageFormat } from "../../../sidePanel/submissionSummarySidePanel/utils/convertFormDateToStorageFormat";
import { CallSummaryMode } from "./callSummaryMode";
import { FaxDocumentUpload } from "./uploadFaxDocument";

/**
 * Trims a form value, returning null when nothing was entered.
 *
 * @param value - Raw form field value
 * @returns Trimmed value, or null so the column is written empty
 */
const orNull = (value: string): string | null => value.trim() || null;

/**
 * Builds the POST body for `pharmacy/appeal/update` from the summary form.
 *
 * Sends every form field except Response Status, which goes to the PA row only.
 * The channel fields follow the open tab: on Fax the number is the fax number
 * and the contact is who it was sent to, on Call they are the phone number and
 * the insurance rep. Follow Up Date is converted to YYYY-MM-DD, matching how
 * dates are stored everywhere else.
 *
 * `appeal_type` and `appeal_reason` are left out so the backend, which skips
 * fields it is not given, leaves them untouched.
 *
 * @param identifier - PA order identifier for the open row
 * @param formData - Appeal summary form values captured on save
 * @param mode - Active channel tab, deciding the phone/fax and contact fields
 * @param attachment - Uploaded fax document, when one was attached
 * @returns Update appeal request payload
 */
export const buildAppealUpdatePayload = (
  identifier: string,
  formData: CallingFormData,
  mode: CallSummaryMode,
  attachment?: FaxDocumentUpload | null,
): UpdateAppealPayload => {
  const isFaxMode = mode === CallSummaryMode.FAX;
  const followUpDate = formData.followUpDate.trim();

  return {
    identifier,
    appeal_status: formData.caseStatus.trim() || undefined,
    appeal_comment: orNull(formData.comment),
    appeal_poc: orNull(formData.callingPoc),
    appeal_insurance_poc: orNull(
      isFaxMode ? formData.sentTo : formData.repName,
    ),
    appeal_case_id: orNull(formData.caseId),
    appeal_reachout_number: orNull(
      isFaxMode ? formData.faxNumber : formData.phoneNumber,
    ),
    appeal_next_action: orNull(formData.nextAction),
    appeal_follow_up_date: followUpDate
      ? convertFormDateToStorageFormat(followUpDate)
      : null,
    ...(attachment
      ? {
          appeal_document_path: attachment.attachment_path,
          appeal_document_name: attachment.attachment_name,
        }
      : {}),
  };
};
